import type { Item } from '../nativeMounting/types'
import type { VanDerValkBomLeg, VanDerValkBomState } from './types'

export type VanDerValkVendorBomLine = {
  amount: number
  articleCode: string
  name: string
}

function isVendorBomLine(x: unknown): x is VanDerValkVendorBomLine {
  if (x == null || typeof x !== 'object') return false
  const o = x as Record<string, unknown>
  const amountRaw = o.amount ?? o.Amount
  const code = o.articleCode ?? o.ArticleCode ?? o.code
  const amount =
    typeof amountRaw === 'number'
      ? amountRaw
      : typeof amountRaw === 'string'
      ? parseInt(amountRaw, 10)
      : NaN
  if (!Number.isFinite(amount) || amount <= 0) return false
  if (typeof code !== 'string' || code.trim() === '') return false
  return true
}

function normalizeVendorBomLine(x: unknown): VanDerValkVendorBomLine | null {
  if (!isVendorBomLine(x)) return null
  const o = x as Record<string, unknown>
  const amountRaw = o.amount ?? o.Amount
  const amount =
    typeof amountRaw === 'number'
      ? Math.round(amountRaw)
      : parseInt(String(amountRaw), 10)
  const articleCode = String(o.articleCode ?? o.ArticleCode ?? o.code ?? '').trim()
  const name = String(o.name ?? o.Name ?? o.description ?? '').trim()
  if (!articleCode || !Number.isFinite(amount) || amount <= 0) return null
  return { amount, articleCode, name }
}

function bomLegToLines(leg: VanDerValkBomLeg | null): VanDerValkVendorBomLine[] {
  if (leg == null) return []
  if ('error' in leg) return []
  const raw = leg.bom
  if (!Array.isArray(raw)) return []
  const out: VanDerValkVendorBomLine[] = []
  for (const row of raw) {
    const line = normalizeVendorBomLine(row)
    if (line) out.push(line)
  }
  return out
}

/**
 * Merge ballast and fixation `bom` arrays; identical `articleCode` lines sum `amount`.
 */
export function mergeVanDerValkVendorBomLines(
  bomState: VanDerValkBomState | null | undefined
): Map<string, { amount: number; description: string }> {
  const merged = new Map<string, { amount: number; description: string }>()
  if (!bomState) return merged

  const lines = [...bomLegToLines(bomState.ballast), ...bomLegToLines(bomState.fixation)]
  for (const { amount, articleCode, name } of lines) {
    const prev = merged.get(articleCode)
    if (prev) {
      merged.set(articleCode, {
        amount: prev.amount + amount,
        description: name || prev.description,
      })
    } else {
      merged.set(articleCode, { amount, description: name })
    }
  }
  return merged
}

/**
 * K2-style lines: `name` = catalog / other-component code (`articleCode`), `components: []`
 * (no roof placement). One logical row per unit (`quantity` / `amount`) so `addItemsToSystem` counts qty correctly.
 * @see generateK2Items in `K2us.ts`
 */
export function vanDerValkMergedBomToMountingItems(merged: Map<string, { amount: number; description: string }>): Item[] {
  return [...merged.entries()].flatMap(([articleCode, { amount }]) =>
    Array.from({ length: amount }, () => ({
      name: articleCode,
      components: [] as Item['components'],
    }))
  )
}

/**
 * Lines from `fetchVanDerValkMountingBom` (already merged server-side). Same item shape as K2-style rows.
 */
export function vanDerValkApiBomLinesToMountingItems(lines: VanDerValkVendorBomLine[]): Item[] {
  const merged = new Map<string, { amount: number; description: string }>()
  for (const row of lines) {
    const amount =
      typeof row.amount === 'number'
        ? Math.round(row.amount)
        : parseInt(String((row as { amount?: unknown }).amount), 10)
    const articleCode = String((row as { articleCode?: unknown }).articleCode ?? '').trim()
    if (!articleCode || !Number.isFinite(amount) || amount <= 0) {
      continue
    }
    const name = String((row as { name?: unknown }).name ?? '').trim()
    const prev = merged.get(articleCode)
    if (prev) {
      merged.set(articleCode, { amount: prev.amount + amount, description: name || prev.description })
    } else {
      merged.set(articleCode, { amount, description: name })
    }
  }
  return vanDerValkMergedBomToMountingItems(merged)
}
